import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {usePeacockSelection} from './usePeacockSelection-hook';

// Stable destructuring defaults — inline `[]`/`() => {}` defaults would
// change identity every render and re-trigger Peacock's effects.
const STABLE_EMPTY_ARRAY = [];
const STABLE_EMPTY_OBJECT = {};
const STABLE_NOOP = () => {};

// Peacock Buttons only accept these variants; anything else misrenders.
const PEACOCK_BUTTON_VARIANTS = new Set([
  'primary',
  'secondary',
  'auxiliary',
  'tertiary',
  'danger'
]);
const toPeacockVariant = (variant) =>
  PEACOCK_BUTTON_VARIANTS.has(variant) ? variant : 'secondary';

/**
 * Encapsulates all business logic for PeacockDataTable: column transformation,
 * stable event handlers, toolbar action building, reset-all, selection model
 * wiring, wrapper-class and select-all summary computation.
 *
 * The component itself is left as a thin render layer that maps the returned
 * values directly onto the Peacock DataTable props.
 */
export const usePeacockDataTable = ({
  columns = [],
  records = [],
  totalRecords = 0,
  paginationConfig = {},
  selectionConfig = {},
  sortingConfig = {},
  filterConfig = {},
  toolbarConfig = {},
  displayConfig = {}
}) => {
  // ── Config destructuring ────────────────────────────────────────────────
  const {
    pageIndex = 0,
    setPageIndex = STABLE_NOOP,
    hidePagination = false,
    onRowsPerPageChange = STABLE_NOOP
  } = paginationConfig;

  const {
    onRowSelect = STABLE_NOOP,
    hideCheckbox = false,
    hideSelectAll = false,
    enableSelectAllPages = false,
    formatSelectAllSummary = null,
    // Seeds the vendor table's internal rowSelection state on mount only —
    // lets callers restore a previous cross-page selection (e.g. reopening
    // a modal) since Peacock's own selection state is otherwise uncontrolled.
    initialRowSelection = STABLE_EMPTY_OBJECT
  } = selectionConfig;

  const {handleSorting = STABLE_NOOP, initialSortConfig = null} = sortingConfig;

  const {
    setSearchArry = STABLE_NOOP,
    onResetAll = STABLE_NOOP,
    hideFilterRow = false,
    showResetAll = false,
    resetAllPosition = 'right',
    resetAllText = 'Reset All',
    disabledHeaders = STABLE_EMPTY_ARRAY
  } = filterConfig;

  const {
    hideToolbar = false,
    toolbarActions = STABLE_EMPTY_ARRAY,
    selectionActions = STABLE_EMPTY_ARRAY,
    rightSideActions = STABLE_EMPTY_ARRAY,
    showDownload = false,
    onDownload = STABLE_NOOP,
    showCreateNew = false,
    onCreateNew = null,
    downloadText = 'Download',
    createNewText = 'Create New',
    showFilterButton = false,
    showMultiSortMenu = false,
    alwaysShowInToolbar = false,
    showActionMenu = false,
    actionItems = STABLE_EMPTY_ARRAY,
    onActionClick = STABLE_NOOP,
    createNewVariant = 'primary',
    downloadIconOnly = false,
    actionMenuVariant = 'text'
  } = toolbarConfig;

  const {
    wrapperClassName = '',
    noDataTitle,
    noDataDescription,
    tableSize,
    maxHeight
  } = displayConfig;

  // ── Ref-forwarding: store every prop function so callbacks are stable ───
  const onResetAllRef = useRef(onResetAll);
  const onDownloadRef = useRef(onDownload);
  const handleSortingRef = useRef(handleSorting);
  const setSearchArryRef = useRef(setSearchArry);
  const setPageIndexRef = useRef(setPageIndex);
  const currentPageIndexRef = useRef(pageIndex);
  const onRowsPerPageChangeRef = useRef(onRowsPerPageChange);
  const enableSelectAllPagesRef = useRef(enableSelectAllPages);

  onResetAllRef.current = onResetAll;
  onDownloadRef.current = onDownload;
  handleSortingRef.current = handleSorting;
  setSearchArryRef.current = setSearchArry;
  setPageIndexRef.current = setPageIndex;
  currentPageIndexRef.current = pageIndex;
  onRowsPerPageChangeRef.current = onRowsPerPageChange;
  enableSelectAllPagesRef.current = enableSelectAllPages;

  // ── Selection model ─────────────────────────────────────────────────────
  const {
    selectionUi,
    selectedRowsRef,
    tableRef,
    handleTableReady: selectionTableReady,
    handleRowSelectionUpdate,
    handleFilterChanged,
    clearSelectionModel,
    getDownloadMeta
  } = usePeacockSelection({
    records,
    totalRecords,
    onRowSelect,
    enabled: enableSelectAllPages,
    mode: 'header'
  });

  // Columns that must not show a header filter. Peacock 3.1.3 still enables
  // filtering by default (omitting filterVariant falls back to text), so these
  // keys are also patched onto columnDef.enableColumnFilter after table ready.
  const filterDisabledColumnKeys = useMemo(() => {
    if (hideFilterRow) return STABLE_EMPTY_ARRAY;
    return columns
      .filter((col) => {
        const header =
          col.header_of_column || col.label_of_column || col.header || '';
        return (
          col.filterable === false ||
          disabledHeaders?.includes(col.label_of_column) ||
          disabledHeaders?.includes(col.header) ||
          disabledHeaders?.includes(col.header_of_column) ||
          disabledHeaders?.includes(header)
        );
      })
      .map((col) => col.name_of_db_column || col.columnKey || '')
      .filter(Boolean);
  }, [columns, disabledHeaders, hideFilterRow]);

  // ── Column transformation ───────────────────────────────────────────────
  const peacockColumns = useMemo(() => {
    return columns.map((col) => {
      const columnKey = col.name_of_db_column || col.columnKey || '';
      const header =
        col.header_of_column || col.label_of_column || col.header || '';
      const isDisabled =
        col.filterable === false ||
        disabledHeaders?.includes(col.label_of_column) ||
        disabledHeaders?.includes(col.header) ||
        disabledHeaders?.includes(col.header_of_column) ||
        disabledHeaders?.includes(header);

      const peacockCol = {
        columnKey,
        header,
        enableSorting: col.hideSortIcon ? false : col.enableSorting !== false,
        initialSize: col.minWidth || col.initialSize || 210
      };

      if (col.cellData) peacockCol.cellData = col.cellData;
      if (col.render) peacockCol.render = col.render;
      if (col.linkHandler) peacockCol.linkHandler = col.linkHandler;
      if (col.linkLabel) peacockCol.linkLabel = col.linkLabel;
      if (col.downloadHandler) peacockCol.downloadHandler = col.downloadHandler;
      if (col.downloadLabel) peacockCol.downloadLabel = col.downloadLabel;

      if (!hideFilterRow && !isDisabled) {
        // Config-driven placeholder for every filter variant (Peacock DS uses
        // columnFilterPlaceholder for text, date, dateRange, and dropdown).
        const placeholder =
          col.columnFilterPlaceholder ||
          col.header_of_column ||
          col.label_of_column ||
          header;

        if (
          col.filterVariant === 'select' ||
          col.filterVariant === 'dropdown'
        ) {
          peacockCol.filterVariant = 'dropdown';
          peacockCol.dropdownFilterOptions = {
            selection: 'single',
            dropdownHeader: header
          };
          peacockCol.columnFilterPlaceholder = placeholder;
        } else if (col.filterVariant === 'dateRange') {
          peacockCol.filterVariant = 'dateRange';
          peacockCol.columnFilterPlaceholder = placeholder;
        } else if (col.filterVariant === 'date') {
          peacockCol.filterVariant = 'date';
          peacockCol.columnFilterPlaceholder = placeholder;
        } else {
          peacockCol.filterVariant = 'text';
          peacockCol.columnFilterPlaceholder = placeholder;
        }
      }

      return peacockCol;
    });
  }, [columns, hideFilterRow, disabledHeaders]);

  // ── Stable handlers ─────────────────────────────────────────────────────

  // Tracks whether the user has explicitly sorted via column header click.
  // Prevents the Peacock table's initial `sortingHandler([])` call on mount
  // from clearing the default sort config.
  const userHasSortedRef = useRef(false);

  // Capture the default sort config from the first render — this never
  // changes.  On first render initialSortConfig equals the static default
  // provided by useDataTable, before any user interaction mutates it.
  const defaultSortConfigRef = useRef(initialSortConfig);

  // Guard ref: when we programmatically call table.setSorting() to restore
  // the default sort, Peacock may re-fire sortingHandler.  This flag lets
  // handleSortingChange skip that echo.
  const isRestoringDefaultSortRef = useRef(false);

  const handleSortingChange = useCallback(
    (sorting) => {
      if (isRestoringDefaultSortRef.current) {
        isRestoringDefaultSortRef.current = false;
        return;
      }
      if (Array.isArray(sorting) && sorting.length > 0) {
        userHasSortedRef.current = true;
        handleSortingRef.current({
          column: sorting[0].column,
          direction: sorting[0].direction
        });
      } else if (userHasSortedRef.current) {
        // Clear sort — third click cycle (asc → desc → clear).
        // Restore the default sort instead of clearing completely so the
        // API keeps receiving the default sort params.
        userHasSortedRef.current = false;
        const defaultSort = defaultSortConfigRef.current;
        if (defaultSort?.column) {
          handleSortingRef.current(defaultSort);
          // Re-apply the visual sort icon for the default column.
          isRestoringDefaultSortRef.current = true;
          tableRef.current?.setSorting([
            {
              id: defaultSort.column,
              desc: defaultSort.direction === 'desc'
            }
          ]);
        } else {
          handleSortingRef.current({});
        }
      }
    },
    [tableRef]
  );

  const handleColumnFiltersChange = useCallback(
    (value, type, columnKey) => {
      if (!columnKey) return;
      // icon and keeps TanStack in sync with the actual (empty) filter.
      if (type === 'dateRange' && value && typeof value === 'object') {
        const hasFrom = value.from != null && value.from !== '';
        const hasTo = value.to != null && value.to !== '';
        if (!hasFrom && !hasTo) {
          const column = tableRef.current?.getColumn(columnKey);
          if (column) column.setFilterValue(undefined);
          // fall through — let normal processing clear the searchArr too
        }
      }

      let processedValue = value ?? '';

      // Peacock DateRangePicker sends {from: Date, to: Date}.
      // Convert to {from: 'YYYY-MM-DD', to: 'YYYY-MM-DD'} strings.
      if (type === 'dateRange' && value && typeof value === 'object') {
        const toYMD = (d) => {
          if (!d) return '';
          if (d instanceof Date && !Number.isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          }
          if (typeof d === 'string') {
            const parsed = new Date(d);
            if (!Number.isNaN(parsed.getTime())) {
              const yyyy = parsed.getFullYear();
              const mm = String(parsed.getMonth() + 1).padStart(2, '0');
              const dd = String(parsed.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            }
          }
          return '';
        };
        processedValue = {from: toYMD(value.from), to: toYMD(value.to)};
      }

      // Peacock DatePicker (single date) may send a Date object OR a
      // formatted string (e.g. "08/20/2026") depending on the DS version.
      // Normalize to 'YYYY-MM-DD' so downstream helpers can parse it.
      if (type === 'date' && value) {
        const toYMDFromDate = (d) => {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
          processedValue = toYMDFromDate(value);
        } else if (typeof value === 'string') {
          // Try parsing the formatted string (e.g. "08/20/2026" or other locale formats)
          const parsed = new Date(value);
          if (!Number.isNaN(parsed.getTime())) {
            processedValue = toYMDFromDate(parsed);
          }
        }
      }

      setSearchArryRef.current({
        field: columnKey,
        value: processedValue,
        type: type || 'string'
      });
      // Reset to first page when any column filter changes.
      setPageIndexRef.current(0);
      // Exits select-all only; explicit selections survive filter edits.
      handleFilterChanged();
    },
    [handleFilterChanged, tableRef]
  );

  // Peacock reports 1-indexed pages — convert to 0-indexed and forward.
  // No dedup guard: Peacock internally calls ze.setPageIndex() first (moving
  // Ne), then fires this callback. A guard comparing against
  // currentPageIndexRef.current (the external prop) would block the very
  // first click when the user is on page 1 (prop=0) and clicks Next, because
  // Peacock emits N(1) (1-indexed) → zeroIndexed=0 === currentPageIndexRef=0.
  const handlePageIndexChange = useCallback((idx) => {
    const zeroIndexed = Math.max(0, idx - 1);
    setPageIndexRef.current(zeroIndexed);
  }, []);

  const handleRowsPerPageChange = useCallback((newPageSize) => {
    onRowsPerPageChangeRef.current(newPageSize);
  }, []);

  // Store initialSortConfig in a ref so handleTableReady stays stable.
  const initialSortConfigRef = useRef(initialSortConfig);
  initialSortConfigRef.current = initialSortConfig;

  // Captures the table instance and disables _autoResetPageIndex — with
  // manualPagination the auto-reset fights the externally controlled page.
  const [isTableReady, setIsTableReady] = useState(false);
  // Bumped in handleResetAllInternal to force-remount the DataTable.
  // Peacock's column filter widgets (DateRangePicker, text inputs) keep
  // their displayed value in uncontrolled/local state that is NOT cleared
  // by TanStack's resetColumnFilters().  Changing the key destroys and
  // recreates the entire DataTable, guaranteeing fresh filter widgets.
  const [filterRowResetKey, setFilterRowResetKey] = useState(0);
  // Bumps after enableColumnFilter patches so the filter row re-reads
  // getCanFilter() (Peacock 3.1.3 does not map enableColumnFilter from cols).
  const [, setFilterCapsVersion] = useState(0);

  const handleTableReady = useCallback(
    (table) => {
      selectionTableReady(table);
      if (table && typeof table._autoResetPageIndex === 'function') {
        table._autoResetPageIndex = () => {};
      }
      setIsTableReady(true);
    },
    [selectionTableReady]
  );

  // NOTE: No table.setPageIndex() sync effect here.
  // With manualPagination=true, Peacock's DataTable already uses the pageIndex
  // prop directly for display (yN = m ? I : ...). Calling table.setPageIndex()
  // from a useEffect re-triggers onPaginationChange internally, which fires
  // onTableReady again, causing cascading re-renders and fighting with
  // Peacock's own internal page state (Ne). The working SalesTargetList /
  // SalesTargetActivitiesList tables never call table.setPageIndex() — they
  // simply pass pageIndex as a prop and let Peacock render it.

  // Apply initial sorting after the table is ready so the sort icon is
  // visible on first render. Uses a state flag + useEffect instead of
  // setTimeout to stay within the React lifecycle.
  useEffect(() => {
    if (!isTableReady) return;
    const table = tableRef.current;
    const sortCfg = initialSortConfigRef.current;
    if (table && sortCfg?.column) {
      table.setSorting([
        {id: sortCfg.column, desc: sortCfg.direction === 'desc'}
      ]);
    }
  }, [isTableReady, tableRef]);

  // Peacock DS never forwards enableColumnFilter; omit-filterVariant still
  // shows a text filter. Patch TanStack column defs, then re-render once.
  useEffect(() => {
    if (!isTableReady) return;
    const table = tableRef.current;
    if (!table || filterDisabledColumnKeys.length === 0) return;

    let didPatch = false;
    filterDisabledColumnKeys.forEach((key) => {
      const column = table.getColumn(key);
      if (column && column.columnDef.enableColumnFilter !== false) {
        column.columnDef.enableColumnFilter = false;
        didPatch = true;
      }
    });

    if (didPatch) {
      setFilterCapsVersion((v) => v + 1);
    }
  }, [isTableReady, filterDisabledColumnKeys, peacockColumns, tableRef]);

  // ── Toolbar actions ─────────────────────────────────────────────────────
  // Peacock Buttons need `text` (not `label`) and a valid variant.
  const selectedRowsActions = useMemo(() => {
    const actions = [];

    if (showDownload && !alwaysShowInToolbar) {
      actions.push({
        text: downloadText,
        variant: 'secondary',
        onClick: () => {
          onDownloadRef.current(selectedRowsRef.current, getDownloadMeta());
        }
      });
    }

    if (showCreateNew && onCreateNew && !alwaysShowInToolbar) {
      actions.push({
        text: createNewText,
        variant: 'secondary',
        onClick: onCreateNew
      });
    }

    // selectionActions: arbitrary buttons shown in the selection banner
    // (right side, same row as the selected-count label). Rendered only when
    // at least one row is selected — identical UX to showDownload/showCreateNew.
    selectionActions.forEach((action) => {
      actions.push({
        text: action.label || action.text || '',
        variant: toPeacockVariant(action.variant),
        disabled: action.disabled,
        onClick: action.onClick || (() => {})
      });
    });

    return actions;
  }, [
    showDownload,
    downloadText,
    showCreateNew,
    onCreateNew,
    createNewText,
    alwaysShowInToolbar,
    selectionActions,
    selectedRowsRef,
    getDownloadMeta
  ]);

  // ── Reset All ───────────────────────────────────────────────────────────
  // Peacock's own reset button never renders without global filter chips, so
  // Reset All ships as a toolbar action and resets filters/selection/sorting/
  // page itself through the table instance.
  const handleResetAllInternal = useCallback(() => {
    onResetAllRef.current();
    tableRef.current?.resetColumnFilters();
    if (enableSelectAllPagesRef.current) {
      clearSelectionModel();
    } else {
      tableRef.current?.resetRowSelection();
    }
    // Restore default sort instead of clearing all sorting.
    // onResetAllRef.current() (useDataTable.handleResetAll) already sets
    // sortingObj back to initialSortConfig for the API.  Here we sync
    // TanStack's visual state to match so the sort icon persists.
    userHasSortedRef.current = false;
    const defaultSort = defaultSortConfigRef.current;
    if (defaultSort?.column) {
      tableRef.current?.setSorting([
        {id: defaultSort.column, desc: defaultSort.direction === 'desc'}
      ]);
    } else {
      tableRef.current?.resetSorting();
    }
    tableRef.current?.setPageIndex(0);
    // Mark table as not-ready so that post-remount effects (initial sort,
    // enableColumnFilter patching) re-run once the new DataTable instance
    // fires onTableReady.  Without this, isTableReady stays true across the
    // key-based remount and the effects silently skip the new table.
    setIsTableReady(false);
    // Force remount of uncontrolled column filter widgets (DateRangePicker
    // etc.) so their displayed value clears along with the actual filter
    // state — see filterRowResetKey declaration above for why this is needed.
    setFilterRowResetKey((k) => k + 1);
  }, [clearSelectionModel, tableRef]);

  const resetFilterOptions = useMemo(
    () =>
      showResetAll
        ? {onFiltersReset: handleResetAllInternal, text: resetAllText}
        : undefined,
    [showResetAll, resetAllText, handleResetAllInternal]
  );

  // Peacock's toolbarActions prop renders buttons inside the DataTable's own
  // toolbar row (always visible, not tied to row selection). This is used for
  // custom actions (e.g. Download Template, Upload Codes, Delete Codes) as
  // well as the optional Reset All button.
  const peacockToolbarActions = useMemo(() => {
    const actions = [];

    toolbarActions.forEach((action) => {
      actions.push({
        text: action.label || action.displayText || action.text || '',
        variant: toPeacockVariant(action.variant),
        disabled: action.disabled,
        onClick: action.onClick || (() => {}),
        iconLeading: action.iconLeading,
        size: action.size
      });
    });

    if (showResetAll) {
      actions.push({
        text: resetAllText,
        variant: 'tertiary',
        onClick: handleResetAllInternal
      });
    }

    return actions.length > 0 ? actions : undefined;
  }, [toolbarActions, showResetAll, resetAllText, handleResetAllInternal]);

  const rightSideActionButton = useMemo(() => {
    const actions = [];

    if (alwaysShowInToolbar) {
      if (showCreateNew && onCreateNew) {
        actions.push({
          text: createNewText,
          variant: toPeacockVariant(createNewVariant),
          onClick: onCreateNew
        });
      }
    }

    // rightSideActions: always-visible toolbar buttons rendered on the right
    // side (same row as Reset All). Positioned between Create New and
    // Download so icon buttons (e.g. gear) appear in the correct order.
    // isDisabled is driven by selection state when requiresSelection is
    // true on the action.
    const hasSelection = selectionUi.selectedCount > 0 || selectionUi.allMode;
    rightSideActions.forEach((action) => {
      const needsSelection = action.requiresSelection !== false;
      actions.push({
        text: action.label || action.text || '',
        variant: toPeacockVariant(action.variant),
        isDisabled: needsSelection ? !hasSelection : !!action.disabled,
        onClick: action.onClick || (() => {}),
        iconOnly: action.iconOnly || false,
        iconName: action.iconName
      });
    });

    if (alwaysShowInToolbar) {
      if (showDownload) {
        actions.push({
          text: downloadIconOnly ? '' : downloadText,
          variant: 'secondary',
          iconOnly: downloadIconOnly,
          iconName: downloadIconOnly ? 'download' : undefined,
          isDisabled: !hasSelection,
          onClick: () => {
            onDownloadRef.current(selectedRowsRef.current, getDownloadMeta());
          }
        });
      }
    }

    return actions.length > 0 ? actions : undefined;
  }, [
    alwaysShowInToolbar,
    showCreateNew,
    onCreateNew,
    createNewText,
    createNewVariant,
    showDownload,
    downloadText,
    downloadIconOnly,
    rightSideActions,
    selectionUi,
    selectedRowsRef,
    getDownloadMeta
  ]);

  const actionMenuConfig = useMemo(
    () =>
      showActionMenu
        ? {
            actionItems,
            onActionClick,
            selectedCount: selectionUi.selectedCount,
            menuButtonVariant: actionMenuVariant
          }
        : undefined,
    [
      showActionMenu,
      actionItems,
      onActionClick,
      selectionUi.selectedCount,
      actionMenuVariant
    ]
  );

  // ── Presentation ────────────────────────────────────────────────────────
  const selectionMode = hideCheckbox ? 'none' : 'multiple';

  const hasLeftToolbarItems =
    (showFilterButton && !hideFilterRow) || showMultiSortMenu;

  const wrapperClasses = [
    'peacock-datatable-wrapper',
    'relative',
    !hasLeftToolbarItems ? 'no-toolbar-divider' : '',
    resetAllPosition === 'left' ? 'toolbar-actions-left' : '',
    selectionUi.allMode ? 'select-all-active' : '',
    !hideCheckbox && hideSelectAll ? 'hide-select-all-header' : '',
    wrapperClassName
  ]
    .filter(Boolean)
    .join(' ');

  // While select-all is armed, the accurate dataset count replaces the
  // library's page-scoped label via the --select-all-summary CSS variable.
  // The last positive total is held so a mid-refetch totalRecords=0 never
  // renders a zero-count summary.
  const lastPositiveTotalRef = useRef(0);
  if (totalRecords > 0) {
    lastPositiveTotalRef.current = totalRecords;
  }

  let selectAllSummary = null;
  if (selectionUi.allMode) {
    const summaryTotal =
      totalRecords > 0 ? totalRecords : lastPositiveTotalRef.current;
    if (summaryTotal > 0) {
      const excludedCount = selectionUi.excludedCount;
      const effectiveCount = Math.max(summaryTotal - excludedCount, 0);
      if (typeof formatSelectAllSummary === 'function') {
        selectAllSummary = formatSelectAllSummary({
          totalRecords: summaryTotal,
          excludedCount,
          effectiveCount
        });
      } else if (excludedCount > 0) {
        selectAllSummary = `${effectiveCount} of ${summaryTotal} selected`;
      } else {
        selectAllSummary = `${summaryTotal} records selected`;
      }
    }
  }

  return {
    // Columns
    peacockColumns,
    // Handlers
    handleSortingChange,
    handleColumnFiltersChange,
    handlePageIndexChange,
    handleRowsPerPageChange,
    handleTableReady,
    handleRowSelectionUpdate,
    // Toolbar
    selectedRowsActions,
    peacockToolbarActions,
    rightSideActionButton,
    actionMenuConfig,
    resetFilterOptions,
    // Selection
    selectionMode,
    selectionUi,
    initialRowSelection,
    // Presentation
    wrapperClasses,
    selectAllSummary,
    // Key-based remount trigger for clearing uncontrolled filter widgets
    filterRowResetKey,
    // Pass-through config values consumed directly in the render
    pageIndex,
    hidePagination,
    hideFilterRow,
    hideToolbar,
    showFilterButton,
    showMultiSortMenu,
    noDataTitle,
    noDataDescription,
    tableSize,
    maxHeight
  };
};
